import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  PreviewFeatureIndicator,
  useFeatureFlags,
} from '@/domains/shared/components/AccountAccess/FeatureFlagsContext';
import {
  betaFeaturesConfig,
  isBetaFeatureEligibleByFlags,
  type BetaFeatureKey,
} from '@/domains/shared/components/AccountAccess/betaFeaturesConfig';

const BETA_FEATURES_STORAGE_KEY = 'beta-feature-preferences';

type BetaFeaturesState = Record<BetaFeatureKey, boolean>;
const DEFAULT_BETA_FEATURES_STATE: BetaFeaturesState = Object.fromEntries(
  Object.entries(betaFeaturesConfig).map(([key, config]) => [
    key,
    config.defaultEnabled,
  ]),
) as BetaFeaturesState;

type BetaFeaturesContextValue = {
  betaFeatures: BetaFeaturesState;
  isHydrated: boolean;
  setBetaFeature: (key: BetaFeatureKey, enabled: boolean) => void;
  isBetaFeatureEnabled: (key: BetaFeatureKey) => boolean;
  isBetaFeatureEligible: (key: BetaFeatureKey) => boolean;
};

const BetaFeaturesContext = createContext<BetaFeaturesContextValue | undefined>(
  undefined,
);

export const BetaFeaturesProvider = ({ children }: { children: ReactNode }) => {
  const [betaFeatures, setBetaFeatures] = useState<BetaFeaturesState>(
    DEFAULT_BETA_FEATURES_STATE,
  );
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsHydrated(true);
      return;
    }

    try {
      const raw = window.sessionStorage.getItem(BETA_FEATURES_STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Partial<BetaFeaturesState>) : {};
      setBetaFeatures({
        ...DEFAULT_BETA_FEATURES_STATE,
        ...parsed,
      } as BetaFeaturesState);
    } catch {
      setBetaFeatures(DEFAULT_BETA_FEATURES_STATE);
    } finally {
      setIsHydrated(true);
    }
  }, []);

  const setBetaFeature = useCallback(
    (key: BetaFeatureKey, enabled: boolean) => {
      setBetaFeatures((previousState) => {
        const nextState = {
          ...previousState,
          [key]: enabled,
        };

        if (typeof window !== 'undefined') {
          try {
            window.sessionStorage.setItem(
              BETA_FEATURES_STORAGE_KEY,
              JSON.stringify(nextState),
            );
          } catch {
            // Keep in-memory state even if persistence fails.
          }
        }

        return nextState;
      });
    },
    [],
  );

  const isBetaFeatureEnabled = useCallback(
    (key: BetaFeatureKey) => betaFeatures[key],
    [betaFeatures],
  );
  const { hasFlag } = useFeatureFlags();
  const isBetaFeatureEligible = useCallback(
    (key: BetaFeatureKey) => isBetaFeatureEligibleByFlags(key, hasFlag),
    [hasFlag],
  );

  const value = useMemo(
    () => ({
      betaFeatures,
      isHydrated,
      setBetaFeature,
      isBetaFeatureEnabled,
      isBetaFeatureEligible,
    }),
    [
      betaFeatures,
      isHydrated,
      isBetaFeatureEnabled,
      isBetaFeatureEligible,
      setBetaFeature,
    ],
  );

  return (
    <BetaFeaturesContext.Provider value={value}>
      {children}
    </BetaFeaturesContext.Provider>
  );
};

export const useBetaFeatures = () => {
  const context = useContext(BetaFeaturesContext);

  if (!context) {
    throw new Error('useBetaFeatures must be used within BetaFeaturesProvider');
  }

  return context;
};

/**
 * ## BetaFeatureWrapper
 *
 * When a new beta feature is registered in `betaFeaturesConfig`,
 * wrap the relevant UI with this component to gate it behind flag eligibility and the
 * user's opt-in preference from the Labs page.
 *
 * ### Usage
 *
 * ```tsx
 * <BetaFeatureWrapper featureKey="myFeature" fallback={<LegacyView />} showIndicator>
 *   <NewView />
 * </BetaFeatureWrapper>
 * ```
 *
 * - Renders `children` when the user is eligible **and** has opted in (mode: `'optIn'`),
 *   or when the user is eligible (mode: `'forced'`).
 * - Renders `fallback` otherwise.
 * - Renders `null` while feature flags or session preferences are loading.
 * - Renders `PreviewFeatureIndicator` above the content when `showIndicator` is true and
 *   `showPreviewIndicatorWhenEnabled` is set on the feature config.
 */
type BetaFeatureWrapperProps = {
  featureKey: BetaFeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
  showIndicator?: boolean;
};

/** @reserved */
export const BetaFeatureWrapper = ({
  featureKey,
  children,
  fallback = null,
  showIndicator = false,
}: BetaFeatureWrapperProps) => {
  const { isLoading, error } = useFeatureFlags();
  const { isHydrated, isBetaFeatureEnabled, isBetaFeatureEligible } =
    useBetaFeatures();

  if (isLoading || !isHydrated) return null;
  if (error) return <>{fallback}</>;

  const config = betaFeaturesConfig[featureKey];
  if (!config) return <>{fallback}</>;

  const isEligible = isBetaFeatureEligible(featureKey);
  const mode = config.mode;
  const shouldShowBetaFeature =
    mode === 'forced'
      ? isEligible
      : isEligible && isHydrated && isBetaFeatureEnabled(featureKey);
  const content = shouldShowBetaFeature ? children : fallback;

  if (!content) return null;

  const shouldShowIndicator =
    showIndicator &&
    shouldShowBetaFeature &&
    config.showPreviewIndicatorWhenEnabled;

  if (shouldShowIndicator) {
    return (
      <div>
        <PreviewFeatureIndicator />
        {content}
      </div>
    );
  }

  return <>{content}</>;
};
